import Dexie, { type Table } from "dexie";
import type {
  QuizConfig,
  QuizAttempt,
  KnowledgeState,
  QuizSession,
  Favorite,
  Journey,
  JourneyFavorite,
  SyncQueueEntry,
  SharedQuizCache,
} from "./types";

/**
 * MapQuizzr IndexedDB Database (v2)
 *
 * Offline-first storage for all user data.
 * See DATA_MODEL.md for complete schema documentation.
 */
export class MapQuizzrDB extends Dexie {
  // Tables
  quiz_configs!: Table<QuizConfig, string>;
  quiz_attempts!: Table<QuizAttempt, string>;
  knowledge_state!: Table<KnowledgeState, string>;
  quiz_sessions!: Table<QuizSession, string>;
  favorites!: Table<Favorite, string>;
  journeys!: Table<Journey, string>;
  journey_favorites!: Table<JourneyFavorite, string>;
  sync_queue!: Table<SyncQueueEntry, string>;
  shared_quizzes_cache!: Table<SharedQuizCache, string>;

  constructor() {
    super("MapQuizzrDB");

    // Schema version 1 (initial)
    this.version(1).stores({
      quiz_configs:
        "signature, category, lastUsedAt, datasetSignature, isPublished",
      quiz_attempts:
        "id, quizSignature, sessionId, [category+region+attemptedAt]",
      knowledge_state: "id, [category+region+granularityLevel], lastUpdated",
      quiz_sessions: "id, quizSignature, completedAt",
      favorites: "signature, createdAt",
      journeys: "id, lastModifiedAt, isPublished",
      journey_favorites: "journeyId, [type+createdAt]",
      sync_queue: "id, [status+createdAt]",
      shared_quizzes_cache: "shareId, cachedAt",
    });

    // Schema version 2 (add lastQuizSignature index for knowledge_state)
    this.version(2).stores({
      knowledge_state: "id, [category+region+granularityLevel], lastUpdated, lastQuizSignature",
    });

    // Schema version 3 (add geography field for deduplication)
    this.version(3).stores({
      quiz_configs:
        "signature, category, geography, lastUsedAt, datasetSignature, isPublished",
      knowledge_state:
        "id, [category+geography+granularityLevel], [category+region+granularityLevel], lastUpdated, lastQuizSignature",
    });

    // Schema version 4 (add displaySettings and difficultyMultiplier to quiz_sessions)
    // Backlog #04: Track Quiz Display Mode in History
    // Note: No index changes needed, just adding new fields to existing records
    this.version(4).stores({
      // Keep all existing table definitions (no changes to indexes)
      quiz_sessions: "id, quizSignature, completedAt",
    });
  }

  /**
   * Clear all data (for testing/reset)
   * CAUTION: This will delete all user data!
   */
  async clearAllData(): Promise<void> {
    await this.transaction(
      "rw",
      [
        this.quiz_configs,
        this.quiz_attempts,
        this.knowledge_state,
        this.quiz_sessions,
        this.favorites,
        this.journeys,
        this.journey_favorites,
        this.sync_queue,
        this.shared_quizzes_cache,
      ],
      async () => {
        await Promise.all([
          this.quiz_configs.clear(),
          this.quiz_attempts.clear(),
          this.knowledge_state.clear(),
          this.quiz_sessions.clear(),
          this.favorites.clear(),
          this.journeys.clear(),
          this.journey_favorites.clear(),
          this.sync_queue.clear(),
          this.shared_quizzes_cache.clear(),
        ]);
      }
    );
  }

  /**
   * Get database storage estimate
   */
  async getStorageEstimate(): Promise<{
    usage: number;
    quota: number;
    percentage: number;
  }> {
    if ("storage" in navigator && "estimate" in navigator.storage) {
      const estimate = await navigator.storage.estimate();
      const usage = estimate.usage ?? 0;
      const quota = estimate.quota ?? 0;
      const percentage = quota > 0 ? (usage / quota) * 100 : 0;

      return { usage, quota, percentage };
    }

    return { usage: 0, quota: 0, percentage: 0 };
  }

  /**
   * Debug: Count records in all tables
   */
  async debugCounts(): Promise<Record<string, number>> {
    return {
      quiz_configs: await this.quiz_configs.count(),
      quiz_attempts: await this.quiz_attempts.count(),
      knowledge_state: await this.knowledge_state.count(),
      quiz_sessions: await this.quiz_sessions.count(),
      favorites: await this.favorites.count(),
      journeys: await this.journeys.count(),
      journey_favorites: await this.journey_favorites.count(),
      sync_queue: await this.sync_queue.count(),
      shared_quizzes_cache: await this.shared_quizzes_cache.count(),
    };
  }
}

// Export singleton instance
export const db = new MapQuizzrDB();

/**
 * Manual migration to add display settings to existing quiz sessions
 * Backlog #04: Track Quiz Display Mode in History
 * Runs outside of Dexie upgrade transaction to avoid timing issues
 */
async function migrateDisplaySettings() {
  const MIGRATION_KEY = 'mapquizzr_display_settings_migration_v4';

  // Check if migration already ran
  if (localStorage.getItem(MIGRATION_KEY) === 'completed') {
    return;
  }

  console.log("🔄 Running display settings migration...");

  try {
    const sessions = await db.quiz_sessions.toArray();
    let updatedSessions = 0;

    for (const session of sessions) {
      // Only update sessions that don't have displaySettings yet
      if (!session.displaySettings) {
        await db.quiz_sessions.update(session.id, {
          displaySettings: {
            hideOutlines: false,
            disableHover: false,
            hideIndicatorColors: false,
            hideLabels: true, // Default is labels off
          },
          difficultyMultiplier: 1.0, // Default difficulty
        });
        updatedSessions++;
      }
    }

    console.log(`✅ Display settings migration complete: Updated ${updatedSessions} quiz sessions`);

    // Mark migration as complete
    localStorage.setItem(MIGRATION_KEY, 'completed');
  } catch (error) {
    console.error("❌ Display settings migration failed:", error);
    // Don't mark as complete so it can retry next time
  }
}

/**
 * Manual migration to populate geography field
 * Runs outside of Dexie upgrade transaction to avoid timing issues
 */
async function migrateGeographyField() {
  const MIGRATION_KEY = 'mapquizzr_geography_migration_v3';

  // Check if migration already ran
  if (localStorage.getItem(MIGRATION_KEY) === 'completed') {
    return;
  }

  console.log("🔄 Running geography field migration...");

  try {
    // Import dataset metadata
    const { geojsonDatasets } = await import("@/data/geojsonDatasets");

    // Create a lookup map for faster access
    const datasetMap = new Map(geojsonDatasets.map(d => [d.path, d.geography]));

    // Migrate quiz_configs
    const quizConfigs = await db.quiz_configs.toArray();
    let updatedQuizConfigs = 0;

    for (const config of quizConfigs) {
      if (!config.geography && config.datasetSource?.type === "dataset-url" && config.datasetSource.path) {
        const geography = datasetMap.get(config.datasetSource.path);
        if (geography) {
          await db.quiz_configs.update(config.signature, { geography });
          updatedQuizConfigs++;
        }
      }
    }

    // Migrate knowledge_state
    const knowledgeStates = await db.knowledge_state.toArray();
    let updatedKnowledgeStates = 0;

    for (const state of knowledgeStates) {
      if (!state.geography) {
        // Find the related quiz config to get geography
        const quizConfig = await db.quiz_configs.get(state.lastQuizSignature);
        if (quizConfig?.geography) {
          await db.knowledge_state.update(state.id, { geography: quizConfig.geography });
          updatedKnowledgeStates++;
        }
      }
    }

    console.log(`✅ Geography migration complete: Updated ${updatedQuizConfigs} quiz configs and ${updatedKnowledgeStates} knowledge states`);

    // Mark migration as complete
    localStorage.setItem(MIGRATION_KEY, 'completed');
  } catch (error) {
    console.error("❌ Geography migration failed:", error);
    // Don't mark as complete so it can retry next time
  }
}

// Run migrations after database opens
db.on('ready', () => {
  // Run migrations asynchronously, don't block database access
  Promise.all([
    migrateDisplaySettings(),
    migrateGeographyField(),
  ]).catch(err => {
    console.error("Migration error:", err);
  });
});

// Handle database errors
db.on("blocked", () => {
  console.warn("⚠️ Database upgrade blocked. Please close other tabs.");
});

db.on("versionchange", () => {
  console.warn("⚠️ Database version changed in another tab.");
  db.close();
});

// Expose debug utilities globally in development
if (import.meta.env.DEV) {
  (window as unknown as { db: MapQuizzrDB }).db = db;
  (window as unknown as { debugDB: () => Promise<void> }).debugDB = async () => {
    console.log("=== MapQuizzr Database Debug ===");
    console.log("Database name:", db.name);
    console.log("Version:", db.verno);

    const counts = await db.debugCounts();
    console.log("Record counts:", counts);

    const storage = await db.getStorageEstimate();
    console.log("Storage usage:", {
      usage: `${(storage.usage / 1024 / 1024).toFixed(2)} MB`,
      quota: `${(storage.quota / 1024 / 1024).toFixed(2)} MB`,
      percentage: `${storage.percentage.toFixed(2)}%`,
    });
  };

  (window as unknown as { clearAllData: () => Promise<void> }).clearAllData =
    async () => {
      if (
        confirm(
          "⚠️ This will delete ALL your quiz data. Are you sure?\n\nThis cannot be undone!"
        )
      ) {
        await db.clearAllData();
        console.log("✅ All data cleared");
        location.reload();
      }
    };

  (window as unknown as { migrateKnowledgeStates: () => Promise<any> }).migrateKnowledgeStates =
    async () => {
      console.log("🔧 Starting knowledge state migration...");

      // 1. Load all quiz configs (for datasetSignature lookup)
      const configs = await db.quiz_configs.toArray();
      console.log(`📋 Loaded ${configs.length} quiz configs`);

      // Log geography distribution to help diagnose issues
      const geographyCount = new Map<string, number>();
      configs.forEach(c => {
        const count = geographyCount.get(c.geography) || 0;
        geographyCount.set(c.geography, count + 1);
      });
      console.log("📊 Geography distribution:", Object.fromEntries(geographyCount));

      // Create lookup map: quizSignature -> config
      const configMap = new Map(configs.map(c => [c.signature, c]));

      // 2. Load all quiz sessions
      const sessions = await db.quiz_sessions.toArray();
      console.log(`📊 Loaded ${sessions.length} quiz sessions`);

      // 3. Group sessions by (category, region, datasetSignature)
      const knowledgeGroups = new Map();

      for (const session of sessions) {
        const config = configMap.get(session.quizSignature);
        if (!config) {
          console.warn(`⚠️ No config found for session ${session.id}, skipping`);
          continue;
        }

        const { category, geography, datasetSignature, title, granularityLevel } = config;
        // Use title as region name (quiz configs don't have a 'region' field)
        const region = title;
        const groupKey = `${category}:${region}:${datasetSignature}`;

        if (!knowledgeGroups.has(groupKey)) {
          knowledgeGroups.set(groupKey, {
            category,
            geography,
            region,
            datasetSignature,
            granularityLevel,
            sessions: [],
          });
        }

        knowledgeGroups.get(groupKey).sessions.push(session);
      }

      console.log(`🗂️ Grouped into ${knowledgeGroups.size} unique knowledge states`);

      // 4. Calculate best knowledge state for each group
      const newKnowledgeStates = [];

      for (const [groupKey, group] of knowledgeGroups.entries()) {
        // Sort sessions by date (most recent last)
        const sortedSessions = group.sessions.sort((a: any, b: any) =>
          new Date(a.completedAt).getTime() - new Date(b.completedAt).getTime()
        );

        const latestSession = sortedSessions[sortedSessions.length - 1];
        const firstSession = sortedSessions[0];

        // Use latest session's metrics
        const totalPrompts = latestSession.totalQuestions;
        const completedPrompts = latestSession.correctAnswers;
        const completionRate = latestSession.accuracyRate;
        const achievedPerfection = completionRate === 100;

        // Find first perfect session (if any)
        const firstPerfectSession = sortedSessions.find((s: any) => s.accuracyRate === 100);
        const perfectCompletionDate = firstPerfectSession ? firstPerfectSession.completedAt : null;

        const knowledgeState = {
          id: groupKey,
          category: group.category,
          geography: group.geography,
          region: group.region,
          datasetSignature: group.datasetSignature,
          granularityLevel: group.granularityLevel,
          totalPrompts,
          completedPrompts,
          completionRate,
          achievedPerfection: achievedPerfection || !!firstPerfectSession,
          perfectCompletionDate,
          lastQuizSignature: latestSession.quizSignature,
          lastUpdated: latestSession.completedAt,
          attemptsCount: sortedSessions.length,
        };

        newKnowledgeStates.push(knowledgeState);
      }

      // 5. Clear old knowledge states and insert new ones
      const oldCount = await db.knowledge_state.count();
      console.log(`🗑️ Clearing ${oldCount} old knowledge states...`);
      await db.knowledge_state.clear();

      console.log(`💾 Inserting ${newKnowledgeStates.length} corrected knowledge states...`);
      await db.knowledge_state.bulkPut(newKnowledgeStates);

      // 6. Verify
      const finalCount = await db.knowledge_state.count();
      console.log(`✅ Migration complete! ${finalCount} knowledge states created.`);

      // 7. Show summary
      console.log("\n📊 Summary:");
      console.log(`   Sessions: ${sessions.length}`);
      console.log(`   Old knowledge states: ${oldCount} (duplicated per-quiz)`);
      console.log(`   New knowledge states: ${finalCount} (grouped by dataset)`);
      console.log(`   Duplicates removed: ${oldCount - finalCount}`);

      return {
        sessionsProcessed: sessions.length,
        oldKnowledgeStates: oldCount,
        newKnowledgeStates: finalCount,
        duplicatesRemoved: oldCount - finalCount,
      };
    };

  (window as unknown as { cleanupUndefinedKnowledge: () => Promise<void> }).cleanupUndefinedKnowledge =
    async () => {
      console.log("🧹 Cleaning up undefined knowledge states...");

      const allStates = await db.knowledge_state.toArray();
      const undefinedStates = allStates.filter(s => s.region === "undefined");

      console.log(`Found ${undefinedStates.length} knowledge states with undefined regions`);

      if (undefinedStates.length === 0) {
        console.log("✅ No undefined knowledge states found. Nothing to clean up!");
        return;
      }

      // Delete all undefined states
      for (const state of undefinedStates) {
        await db.knowledge_state.delete(state.id);
      }

      const remainingCount = await db.knowledge_state.count();
      console.log(`✅ Cleanup complete! Deleted ${undefinedStates.length} undefined states. ${remainingCount} knowledge states remaining.`);
      console.log("💡 Now run: await window.migrateKnowledgeStates() to rebuild knowledge states correctly");
    };

  console.log(
    "💡 Debug utilities available:\n" +
      "  - window.db - Database instance\n" +
      "  - window.debugDB() - Show database stats\n" +
      "  - window.clearAllData() - Clear all data (with confirmation)\n" +
      "  - window.migrateKnowledgeStates() - Fix duplicate knowledge states\n" +
      "  - window.cleanupUndefinedKnowledge() - Delete knowledge states with undefined regions"
  );
}
